import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getSelectedAccountsApiV1ExternalAccountsSelectedGet } from "@/backend-sdk/client/sdk.gen";
import { heyApiClient, tenantAtivo } from "@/lib/hey-api-client";
import { useAccountIntegrationsData } from "@/hooks/useAccountIntegrationsData";
import {
  estadoDasFontes,
  type EstadoDasFontes,
  type FamiliaDeConexao,
} from "@/lib/motivo-do-indisponivel";

/**
 * Prefixo das consultas sobre contas escolhidas da loja — quem grava a seleção invalida o
 * prefixo, e toda consulta pendurada nele (esta, e a conta do Instagram) é refeita.
 */
export const CHAVE_DAS_CONTAS_SELECIONADAS = "contas-externas-selecionadas";

/** Um N/D novo na tela não precisa buscar de novo o que outro N/D acabou de buscar. */
const VALIDADE_MS = 60_000;

/**
 * O que a loja ativa tem configurado, para explicar um N/D.
 *
 * Uma consulta por loja, compartilhada por todos os N/D da tela (o React Query deduplica a
 * chave e, com a validade, não refaz a busca a cada N/D que aparece). O estado de token vem
 * de `useAccountIntegrationsData`, com a mesma validade.
 *
 * <D-089> A loja vai em `customer_uuid`, a mesma que as rotas de dados da tela recebem. A
 * rota a lia do claim `account_id` do token de conta, e esta consulta só rodava quando ele e
 * a tela concordavam; quando esse token vencia, com o painel parado, a chamada saía com o
 * token de login e a resposta era `400 Conta não informada`.
 *
 * `null` enquanto isso não se sabe (sem loja ativa, lista não voltou ou falhou): quem mostra
 * o motivo não deve afirmar nada sobre integração sem saber.
 */
export function useEstadoDasFontes(): EstadoDasFontes | null {
  const loja = tenantAtivo();

  const contas = useQuery({
    queryKey: [CHAVE_DAS_CONTAS_SELECIONADAS, "tipos", loja],
    queryFn: async () => {
      const { data } =
        await getSelectedAccountsApiV1ExternalAccountsSelectedGet({
          client: heyApiClient,
          throwOnError: true,
          query: { customer_uuid: loja },
        });
      return data.accounts.map((conta) => conta.type);
    },
    enabled: Boolean(loja),
    staleTime: VALIDADE_MS,
  });

  const { tokenIssues } = useAccountIntegrationsData({
    staleTime: VALIDADE_MS,
  });
  const tiposDeConta = loja ? contas.data : undefined;

  return useMemo(() => {
    if (!tiposDeConta) return null;
    const expiradas = (Object.keys(tokenIssues) as FamiliaDeConexao[]).filter(
      (familia) => tokenIssues[familia] !== null,
    );
    return estadoDasFontes(tiposDeConta, expiradas);
  }, [tiposDeConta, tokenIssues]);
}
